import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import BrandMark, { LogoFull } from "@/components/BrandMark";
import {
  ArrowRight, ArrowUpRight, Play, LineChart, MapPin, FileText, Clock, Users,
  Camera, BarChart3, Lock, Smartphone, CheckCircle2, TrendingUp, Layers,
  ShieldCheck, Globe, Bell,
} from "lucide-react";

// ============================================================
// DATA
// ============================================================
const STATS = [
  { value: 700, suffix: "+", label: "Proiecte livrate" },
  { value: 3.2, suffix: "M+", label: "mp construiți", decimal: true },
  { value: 10, suffix: "+", label: "țări acoperite" },
  { value: 98, suffix: "%", label: "livrare la termen" },
];

const CLIENTS = [
  "Kaufland", "Lidl", "IKEA", "WDP", "McDonald's", "NEPI Rockcastle",
  "Penny", "Raiffeisen", "Patria Bank", "Mega Image", "Coca-Cola", "BT",
  "Fressnapf", "Alesonor", "Carmistin", "UVT",
];

const ACCESS_POINTS = [
  { icon: Lock, title: "Izolare completă a datelor", text: "Fiecare client vede exclusiv proiectele proprii. Autentificare securizată, sesiuni httpOnly." },
  { icon: Smartphone, title: "Mobile-ready", text: "Acces complet din telefon și tabletă, cu aceleași date și funcționalități ca pe desktop." },
  { icon: Layers, title: "Portofolii multi-proiect", text: "Zeci de proiecte simultane, filtrare și sortare instant, o singură autentificare." },
  { icon: Bell, title: "Actualizări permanente", text: "Rapoartele lunare, pozele de șantier și progresul se publică direct de echipa de proiect." },
];

// ============================================================
// COUNT-UP COMPONENT
// ============================================================
function CountUp({ end, decimal = false, suffix = "", duration = 2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min((t - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(eased * end);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);
  return (
    <span ref={ref} className="tabular-nums">
      {decimal ? val.toFixed(1) : Math.round(val)}
      {suffix}
    </span>
  );
}

// ============================================================
// LANDING
// ============================================================
export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-ink">
      {/* ============================ NAV ============================ */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-xl border-b border-neutral-200" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="group" data-testid="nav-logo">
            <LogoFull
              variant={scrolled ? "dark" : "light"}
              size="md"
              className="transition-transform group-hover:scale-[1.03] duration-500"
            />
          </Link>
          <nav className={`hidden md:flex items-center gap-10 text-sm font-medium transition-colors ${scrolled ? "text-ink" : "text-white"}`}>
            <a href="#portal" className="nav-link" style={{ color: "inherit" }}>Portal client</a>
            <a href="#functionalitati" className="nav-link" style={{ color: "inherit" }}>Funcționalități</a>
            <a href="#acces" className="nav-link" style={{ color: "inherit" }}>Acces & securitate</a>
            <a href="#contact" className="nav-link" style={{ color: "inherit" }}>Contact</a>
          </nav>
          <Link to="/login" data-testid="nav-login" className="btn-brand">
            Autentificare <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      {/* ============================ HERO ============================ */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden bg-ink text-white">
        {/* background image */}
        <motion.div className="absolute inset-0" style={{ y: heroY, scale: heroScale }}>
          <img
            src="https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=2400&q=90"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/95 via-ink/70 to-ink/95" />
        </motion.div>
        <div className="absolute inset-0 grid-lines pointer-events-none opacity-60" />

        {/* Giant floating logo (decorative) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 0.08, scale: 1 }}
          transition={{ duration: 1.5, ease: [0.2, 0.7, 0.2, 1] }}
          className="absolute right-[-8%] top-1/3 pointer-events-none hidden lg:block"
        >
          <BrandMark variant="light" className="h-72" />
        </motion.div>

        {/* Dashed rotating circle */}
        <div className="absolute right-24 top-32 hidden xl:block pointer-events-none">
          <div className="spin-slow">
            <svg width="180" height="180" viewBox="0 0 180 180">
              <circle cx="90" cy="90" r="85" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="2 8" />
            </svg>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 text-center leading-tight">
              Since<br />2016
            </span>
          </div>
        </div>

        <div className="relative w-full max-w-[1440px] mx-auto px-6 lg:px-10 pt-24 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-12 bg-brand" />
            <span className="font-mono text-white/70 text-[11px] uppercase tracking-[0.3em]">
              Since 2016 · Bucharest, London, Chișinău
            </span>
          </motion.div>

          <div className="max-w-5xl">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.2, 0.7, 0.2, 1] }}
              className="font-display font-black text-white text-6xl sm:text-7xl md:text-[7.5rem] lg:text-[9.5rem] leading-[0.85] tracking-tighter"
            >
              Construim<br />
              <span className="text-white/95">predictibilitate</span><br />
              <span className="inline-block italic font-medium text-brand relative">
                pentru clienți.
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1.2, delay: 1, ease: [0.7, 0, 0.2, 1] }}
                  className="absolute -bottom-2 left-0 right-0 h-1 bg-brand origin-left"
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-10 text-white/70 text-lg md:text-xl max-w-2xl leading-relaxed"
            >
              Partenerul tău pentru proiecte ambițioase. Combinăm servicii de project, cost și construction management cu
              o platformă digitală completă, care îți oferă acces 24/7 la toate informațiile esențiale ale proiectului:
              contracte, rapoarte, planuri de costuri, documente, fotografii, termene și etape de progres.
              Totul este centralizat, actualizat și ușor de urmărit, pentru ca tu să ai permanent control,
              transparență și o imagine clară asupra proiectului.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-12 flex flex-wrap items-center gap-4"
            >
              <Link to="/login" data-testid="hero-login" className="btn-brand">
                Accesează portalul <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#portal" className="btn-ghost">
                <Play className="h-4 w-4" /> Descoperă platforma
              </a>
            </motion.div>
          </div>
        </div>

        {/* Bottom scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-8 w-px bg-white/30"
          />
        </div>
      </section>

      {/* ============================ STATS ============================ */}
      <section id="stats" className="relative bg-white border-b border-neutral-200 py-24 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-4 gap-8 md:gap-4">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.7 }}
                className="border-l-2 border-brand pl-6 md:pl-8"
              >
                <div className="font-display font-black text-5xl md:text-6xl lg:text-7xl text-ink leading-none">
                  <CountUp end={s.value} suffix={s.suffix} decimal={s.decimal} />
                </div>
                <div className="mt-4 font-mono text-[11px] uppercase tracking-widest text-ink-muted">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ CLIENTS MARQUEE ============================ */}
      <section className="border-y border-neutral-200 py-12 overflow-hidden bg-ink-soft">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 mb-8 flex items-center justify-between">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ink-muted">
            — Ne-au încredințat proiectele lor
          </p>
          <span className="font-mono text-[11px] text-ink-muted hidden sm:inline">120+ clienți enterprise</span>
        </div>
        <div className="marquee">
          <div className="flex gap-16 whitespace-nowrap items-center pr-16 shrink-0">
            {[...CLIENTS, ...CLIENTS].map((c, i) => (
              <span key={i} className="font-display font-bold text-3xl text-ink-muted/60 hover:text-brand transition-colors cursor-default">
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ PORTAL INTRO ============================ */}
      <section id="portal" className="relative py-32 lg:py-40 bg-ink text-white overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 mb-20">
            <div className="lg:col-span-6">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-4">— Portal client</div>
              <h2 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.9]">
                Transparență totală.<br />
                <span className="italic font-medium text-brand">Control în timp real.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-white/70 text-lg leading-relaxed">
                Platforma digitală Brisk oferă clienților acces permanent la stadiul proiectului: progres, costuri,
                documente și rapoarte — actualizate de echipa de proiect, disponibile 24/7 din browser sau de pe mobil.
              </p>
            </div>
          </div>

          {/* Dashboard mockup */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="relative"
          >
            <div className="bg-white/[0.04] border border-white/10 p-6 md:p-10 backdrop-blur-sm">
              <div className="bg-white border border-neutral-200 text-ink shadow-2xl">
                {/* Top bar */}
                <div className="h-14 border-b border-neutral-100 flex items-center px-6 gap-3">
                  <div className="flex gap-1.5">
                    <div className="h-3 w-3 rounded-full bg-red-400" />
                    <div className="h-3 w-3 rounded-full bg-yellow-400" />
                    <div className="h-3 w-3 rounded-full bg-green-400" />
                  </div>
                  <div className="ml-6 flex-1 max-w-md bg-ink-soft h-8 rounded-sm flex items-center px-3 text-xs font-mono text-ink-muted">
                    portal.briskgrp.com/proiecte
                  </div>
                  <BrandMark variant="dark" className="h-5" />
                </div>
                {/* Content */}
                <div className="grid md:grid-cols-4 gap-4 p-6">
                  {[
                    { label: "Total proiecte", val: "4", color: "text-ink" },
                    { label: "Active", val: "3", color: "text-brand", accent: true },
                    { label: "Finalizate", val: "1", color: "text-ink" },
                    { label: "Progres mediu", val: "52%", color: "text-ink" },
                  ].map((s) => (
                    <div key={s.label} className={`p-5 border ${s.accent ? "border-brand bg-brand/5" : "border-neutral-200"}`}>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{s.label}</div>
                      <div className={`mt-2 font-display font-black text-3xl ${s.color}`}>{s.val}</div>
                    </div>
                  ))}
                </div>
                <div className="p-6 pt-0 grid md:grid-cols-2 gap-4">
                  {[
                    { code: "BRP-2024-001", name: "Reședința Băneasa Gardens", loc: "București", prog: 62 },
                    { code: "BRP-2024-002", name: "Centru Logistic Ploiești", loc: "Prahova", prog: 34 },
                  ].map((p) => (
                    <div key={p.code} className="border border-neutral-200 p-5 flex gap-4 hover:border-brand transition-colors">
                      <div className="h-16 w-24 bg-ink-soft shrink-0 overflow-hidden">
                        <img src="https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=400&q=60" alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[10px] text-ink-muted">{p.code}</span>
                          <span className="px-2 py-0.5 bg-brand text-white text-[9px] font-mono uppercase tracking-widest">Activ</span>
                        </div>
                        <div className="mt-1 font-display font-bold text-sm truncate">{p.name}</div>
                        <div className="mt-3 flex items-center gap-2">
                          <div className="h-1 flex-1 bg-neutral-100">
                            <div className="h-full bg-brand" style={{ width: `${p.prog}%` }} />
                          </div>
                          <span className="font-mono text-[11px] text-brand font-bold">{p.prog}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Floating accent card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.7 }}
              className="absolute -bottom-12 -right-2 md:-right-6 bg-brand text-white p-6 md:p-8 shadow-2xl hidden md:block max-w-xs"
            >
              <TrendingUp className="h-6 w-6" />
              <div className="mt-4 font-display font-black text-3xl">+42%</div>
              <div className="mt-2 font-mono text-[11px] uppercase tracking-widest text-white/80">
                economie de timp în coordonarea proiectelor
              </div>
            </motion.div>
          </motion.div>

          {/* Trust chips */}
          <div className="mt-20 flex flex-wrap gap-3">
            {["Acces 24/7", "Date izolate per client", "Fără instalare — direct din browser", "Inclus în serviciile Brisk"].map((chip) => (
              <span key={chip} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white/70">
                <CheckCircle2 className="h-3.5 w-3.5 text-brand" /> {chip}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ BENTO FEATURES ============================ */}
      <section id="functionalitati" className="py-32 lg:py-40 bg-ink-soft">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-6">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-4">— Funcționalități</div>
              <h2 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.9]">
                Toate datele proiectului,<br />într-un singur loc.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-ink-muted text-lg leading-relaxed">
                Progres, documente, costuri, fotografii și contactele echipei — organizate pe proiect,
                actualizate permanent și accesibile fără solicitări prin email.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:auto-rows-fr">
            {/* Big card — progress */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7 }}
              data-testid="feature-progress-card"
              className="lg:col-span-8 lg:row-span-2 group bg-ink text-white p-8 lg:p-12 relative overflow-hidden card-lift"
            >
              <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
              <div className="relative flex flex-col h-full">
                <div className="h-12 w-12 bg-brand text-white flex items-center justify-center">
                  <BarChart3 className="h-6 w-6" />
                </div>
                <h3 className="mt-8 font-display font-bold text-3xl lg:text-4xl tracking-tight leading-tight">
                  Progres în timp real
                </h3>
                <p className="mt-4 text-white/70 leading-relaxed max-w-lg">
                  Procentul de execuție pe fiecare fază, milestone-uri contractuale cu status live și
                  timeline actualizat de echipa de proiect, după fiecare raportare.
                </p>
                {/* Mini progress UI */}
                <div className="mt-auto pt-10 space-y-4 max-w-xl">
                  {[
                    { label: "Structură", pct: 100, done: true },
                    { label: "Închideri & fațade", pct: 74 },
                    { label: "Instalații MEP", pct: 41 },
                    { label: "Finisaje", pct: 12 },
                  ].map((ph) => (
                    <div key={ph.label} className="flex items-center gap-4">
                      <span className="font-mono text-[11px] uppercase tracking-widest text-white/60 w-40 shrink-0">{ph.label}</span>
                      <div className="h-1.5 flex-1 bg-white/10">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${ph.pct}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.2, ease: [0.2, 0.7, 0.2, 1] }}
                          className={`h-full ${ph.done ? "bg-emerald-400" : "bg-brand"}`}
                        />
                      </div>
                      <span className="font-mono text-xs font-bold text-white/80 w-10 text-right">{ph.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Documents */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: 0.1, duration: 0.7 }}
              data-testid="feature-documents-card"
              className="lg:col-span-4 group bg-white border border-neutral-200 hover:border-brand p-8 card-lift"
            >
              <div className="h-11 w-11 bg-brand/10 text-brand flex items-center justify-center group-hover:bg-brand group-hover:text-white transition-colors">
                <FileText className="h-5 w-5" />
              </div>
              <h4 className="mt-6 font-display font-bold text-xl tracking-tight">Seif de documente</h4>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                Contracte, acte adiționale și rapoarte lunare — descărcabile în PDF, Word și Excel, ordonate pe proiect.
              </p>
            </motion.div>

            {/* Photo gallery */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: 0.2, duration: 0.7 }}
              data-testid="feature-gallery-card"
              className="lg:col-span-4 group bg-white border border-neutral-200 hover:border-brand p-8 card-lift"
            >
              <div className="h-11 w-11 bg-brand/10 text-brand flex items-center justify-center group-hover:bg-brand group-hover:text-white transition-colors">
                <Camera className="h-5 w-5" />
              </div>
              <h4 className="mt-6 font-display font-bold text-xl tracking-tight">Galerie foto lunară HD</h4>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                Evoluția șantierului în imagini de înaltă rezoluție, organizate pe lună și disciplină.
              </p>
            </motion.div>

            {/* Budget */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: 0.1, duration: 0.7 }}
              data-testid="feature-budget-card"
              className="lg:col-span-6 group bg-white border border-neutral-200 hover:border-brand p-8 card-lift flex items-start gap-6"
            >
              <div className="h-11 w-11 shrink-0 bg-brand/10 text-brand flex items-center justify-center group-hover:bg-brand group-hover:text-white transition-colors">
                <LineChart className="h-5 w-5" />
              </div>
              <div>
                <h4 className="font-display font-bold text-xl tracking-tight">Raportare buget & costuri</h4>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                  Transparență financiară completă: execuție bugetară, previziuni de cost și rapoarte
                  aliniate cu scopul și programul proiectului.
                </p>
              </div>
            </motion.div>

            {/* Team contact */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: 0.2, duration: 0.7 }}
              data-testid="feature-team-card"
              className="lg:col-span-6 group bg-white border border-neutral-200 hover:border-brand p-8 card-lift flex items-start gap-6"
            >
              <div className="h-11 w-11 shrink-0 bg-brand/10 text-brand flex items-center justify-center group-hover:bg-brand group-hover:text-white transition-colors">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <h4 className="font-display font-bold text-xl tracking-tight">Contact direct cu echipa</h4>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                  Project Manager, Cost Manager și Site Supervisor — email și telefon, direct din portal,
                  pentru fiecare proiect în parte.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================ ACCESS & SECURITY ============================ */}
      <section id="acces" className="py-32 lg:py-40 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-4">— Acces & securitate</div>
              <h2 className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.9]">
                Securitate<br />enterprise.<br />
                <span className="italic font-medium text-brand">De oriunde.</span>
              </h2>
              <p className="mt-8 text-ink-muted text-lg leading-relaxed max-w-xl">
                Datele fiecărui client sunt izolate și protejate, indiferent dacă gestionezi un singur proiect
                sau un portofoliu complex. Portalul este optimizat pentru mobil, tabletă și desktop.
              </p>
              <div className="mt-12 space-y-8">
                {ACCESS_POINTS.map((a, i) => {
                  const Icon = a.icon;
                  return (
                    <motion.div
                      key={a.title}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1, duration: 0.6 }}
                      className="flex items-start gap-5"
                    >
                      <div className="h-11 w-11 shrink-0 bg-brand/10 text-brand flex items-center justify-center">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="font-display font-bold text-lg tracking-tight">{a.title}</h4>
                        <p className="mt-1 text-sm text-ink-muted leading-relaxed max-w-md">{a.text}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
            <div className="lg:col-span-6 relative">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9 }}
                className="relative"
              >
                <div className="aspect-[4/5] overflow-hidden bg-ink-soft">
                  <img
                    src="https://images.unsplash.com/photo-1778074762033-c6595907684d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                    alt="Echipă de construcții consultând portalul pe tabletă"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
                </div>
                {/* Floating badge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.7 }}
                  className="absolute -bottom-8 -left-4 md:-left-10 bg-ink text-white p-6 md:p-8 shadow-2xl max-w-xs"
                >
                  <ShieldCheck className="h-6 w-6 text-brand" />
                  <div className="mt-4 font-display font-bold text-lg leading-tight">Date criptate & izolate per client</div>
                  <div className="mt-2 font-mono text-[11px] uppercase tracking-widest text-white/60">
                    zero acces încrucișat între clienți
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6, duration: 0.7 }}
                  className="absolute -top-6 -right-4 md:-right-8 bg-white border border-neutral-200 p-5 shadow-xl hidden md:flex items-center gap-4"
                >
                  <Globe className="h-5 w-5 text-brand" />
                  <div>
                    <div className="font-display font-black text-2xl leading-none">24/7</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-1">acces global</div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ CTA ============================ */}
      <section id="contact" className="relative py-32 lg:py-40 bg-ink text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.pexels.com/photos/25461690/pexels-photo-25461690.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/70 to-ink" />
        <div className="absolute inset-0 grid-lines opacity-40" />

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="max-w-4xl">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-6">— Începe astăzi</div>
            <h2 className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
              Începe<br />
              următorul proiect<br />
              <span className="italic font-medium text-brand">cu Brisk.</span>
            </h2>
            <p className="mt-10 text-white/70 text-lg md:text-xl max-w-2xl leading-relaxed">
              Clienții Brisk primesc acces la portal odată cu semnarea contractului. Programează o discuție
              de 30 de minute cu un Project Director și primești o propunere personalizată în maxim 48 de ore.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link to="/login" data-testid="cta-login" className="btn-brand">
                Intră în portal <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="mailto:office@briskgrp.com" data-testid="cta-contact" className="btn-ghost">
                Contactează un consultant <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ FOOTER ============================ */}
      <footer className="bg-ink text-white/70 border-t border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <LogoFull variant="light" size="xl" className="mb-6" />
            <p className="text-sm leading-relaxed max-w-sm">
              Project, Cost și Construction Management de nivel enterprise. Design integrat & MEP Engineering.
              Din 2016, în București, Londra și Chișinău.
            </p>
          </div>
          <div className="md:col-span-2">
            <h4 className="font-mono uppercase text-[10px] tracking-widest text-white mb-5">Servicii</h4>
            <ul className="text-sm space-y-3">
              <li><a href="https://www.briskgrp.com/services" target="_blank" rel="noreferrer" className="hover:text-brand transition-colors">Project Management</a></li>
              <li><a href="https://www.briskgrp.com/services" target="_blank" rel="noreferrer" className="hover:text-brand transition-colors">Construction Management</a></li>
              <li><a href="https://www.briskgrp.com/services" target="_blank" rel="noreferrer" className="hover:text-brand transition-colors">Cost Management</a></li>
              <li><a href="https://www.briskgrp.com/services" target="_blank" rel="noreferrer" className="hover:text-brand transition-colors">Architecture & MEP</a></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <h4 className="font-mono uppercase text-[10px] tracking-widest text-white mb-5">Portal</h4>
            <ul className="text-sm space-y-3">
              <li><a href="#portal" className="hover:text-brand transition-colors">Portal client</a></li>
              <li><a href="#functionalitati" className="hover:text-brand transition-colors">Funcționalități</a></li>
              <li><a href="#acces" className="hover:text-brand transition-colors">Acces & securitate</a></li>
              <li><Link to="/login" className="hover:text-brand transition-colors">Autentificare</Link></li>
            </ul>
          </div>
          <div className="md:col-span-4">
            <h4 className="font-mono uppercase text-[10px] tracking-widest text-white mb-5">Contact</h4>
            <ul className="text-sm space-y-3">
              <li className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-brand" /> București · Londra · Chișinău</li>
              <li><a href="mailto:office@briskgrp.com" className="hover:text-brand transition-colors">office@briskgrp.com</a></li>
              <li><a href="tel:+40700000000" className="hover:text-brand transition-colors">+40 700 000 000</a></li>
            </ul>
            <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-brand hover:text-white transition-colors text-sm font-medium">
              Accesează portalul clienților <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-6 flex flex-wrap justify-between items-center text-[11px] text-white/50 font-mono uppercase tracking-widest gap-4">
            <div>© 2026 Brisk Group Consulting & Development. Toate drepturile rezervate.</div>
            <div>Made with precision · Since 2016</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
